/**
 * CoachPersonaPicker — modal-style picker for the Mirror coach. Lets the
 * user filter by industry + gender, audition voices, and save their
 * persona + voice + auto-speak + mute preferences.
 *
 * Hybrid scoping: org admins see a "Set as org default" affordance;
 * regular users see only "Save as my preference". Server resolves the
 * effective persona on every load (user > org > app default).
 */
import React, { useEffect, useMemo, useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import { toast } from 'sonner';
import {
  X, Volume2, VolumeX, Check, Loader2, Play, Square, Sparkles, RotateCcw,
} from 'lucide-react';
import { Button } from '../ui/button';
import CoachAvatar from './CoachAvatar';
import useCoachVoice, { suggestVoiceUri } from '../../hooks/useCoachVoice';

const API = process.env.REACT_APP_BACKEND_URL;

const GENDER_FILTERS = [
  { id: 'any', label: 'Any' },
  { id: 'female', label: 'Female-coded' },
  { id: 'male', label: 'Male-coded' },
  { id: 'non_binary', label: 'Non-binary' },
];

const INDUSTRY_FILTERS = [
  { id: 'any', label: 'Any' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'financial_services', label: 'Financial Svcs' },
  { id: 'manufacturing', label: 'Manufacturing' },
  { id: 'technology', label: 'Technology' },
  { id: 'retail', label: 'Retail' },
  { id: 'professional_services', label: 'Prof Svcs' },
];

const SAMPLE_LINE = "Hi there. I'm here to coach you through your next step. What would you like to work on today?";

export default function CoachPersonaPicker({
  open, onClose, currentPrefs, currentOrgId, isOrgAdmin, onSaved,
}) {
  const [personas, setPersonas] = useState([]);
  const [genderFilter, setGenderFilter] = useState('any');
  const [industryFilter, setIndustryFilter] = useState('any');
  const [selectedId, setSelectedId] = useState(currentPrefs?.persona_id || null);
  const [voiceUri, setVoiceUri] = useState(currentPrefs?.voice_uri || null);
  const [voiceRate, setVoiceRate] = useState(currentPrefs?.voice_rate || 1.0);
  const [autoSpeak, setAutoSpeak] = useState(currentPrefs?.auto_speak ?? true);
  const [muted, setMuted] = useState(currentPrefs?.muted || false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const { voices, speak, cancel, speaking, supported } = useCoachVoice();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelectedId(currentPrefs?.persona_id || null);
    setVoiceUri(currentPrefs?.voice_uri || null);
    setVoiceRate(currentPrefs?.voice_rate || 1.0);
    setAutoSpeak(currentPrefs?.auto_speak ?? true);
    setMuted(currentPrefs?.muted || false);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (industryFilter !== 'any') params.set('industry', industryFilter);
        if (genderFilter !== 'any') params.set('gender', genderFilter);
        const r = await authedFetch(`${API}/api/coach/personas?${params.toString()}`);
        if (r.ok) {
          const d = await r.json();
          setPersonas(d.personas || []);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => cancel();
  }, [open, industryFilter, genderFilter, currentPrefs, cancel]);

  const selectedPersona = personas.find((p) => p.id === selectedId) || null;

  // Suggest a default voice for the selected persona — used the first
  // time a user lands on a persona without a manual voice pick. Once
  // they touch the dropdown the suggestion stops auto-overriding.
  useEffect(() => {
    if (!selectedPersona || voiceUri) return;
    const suggested = suggestVoiceUri(voices, selectedPersona.voice_hint);
    if (suggested) setVoiceUri(suggested);
  }, [selectedPersona, voices, voiceUri]);

  const testVoice = () => {
    if (muted) {
      toast.info('Voice is muted — unmute to hear the sample');
      return;
    }
    if (speaking) {
      cancel();
      return;
    }
    speak(SAMPLE_LINE, { voiceUri, rate: voiceRate, muted: false });
  };

  const save = async ({ asOrgDefault = false } = {}) => {
    if (!selectedId) {
      toast.error('Pick a coach first');
      return;
    }
    setSaving(true);
    try {
      // Always save the per-user prefs (voice, rate, auto-speak, mute, persona).
      const r = await authedFetch(`${API}/api/coach/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona_id: selectedId,
          voice_uri: voiceUri,
          voice_rate: voiceRate,
          auto_speak: autoSpeak,
          muted,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      // If the admin also asked to make this the org default, fire that.
      if (asOrgDefault && isOrgAdmin && currentOrgId) {
        const r2 = await authedFetch(`${API}/api/coach/org-default/${currentOrgId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ persona_id: selectedId }),
        });
        if (!r2.ok) throw new Error(await r2.text());
        toast.success(`Saved · org default set to ${selectedPersona?.name}`);
      } else {
        toast.success('Coach preferences saved');
      }
      onSaved?.(data.resolved);
      onClose?.();
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const resetToOrgDefault = async () => {
    setSaving(true);
    try {
      const r = await authedFetch(`${API}/api/coach/preferences/persona`, { method: 'DELETE' });
      if (!r.ok) throw new Error(await r.text());
      const resolved = await r.json();
      toast.success(`Reset — using ${resolved.source === 'org' ? 'org default' : 'app default'}`);
      onSaved?.(resolved);
      onClose?.();
    } catch (e) {
      toast.error(`Reset failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      data-testid="coach-persona-picker"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-900">Coach settings</h2>
            {currentPrefs?.source && (
              <span className="text-[10px] font-mono text-slate-500 ml-1">
                · source: {currentPrefs.source}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            data-testid="coach-picker-close"
            aria-label="Close coach settings"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Industry</span>
            <select
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs"
              data-testid="coach-picker-industry-filter"
            >
              {INDUSTRY_FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Gender</span>
            <select
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs"
              data-testid="coach-picker-gender-filter"
            >
              {GENDER_FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          {currentPrefs?.source === 'user' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToOrgDefault}
              className="ml-auto text-xs"
              data-testid="coach-picker-reset"
            >
              <RotateCcw className="h-3 w-3 mr-1" /> Reset to org default
            </Button>
          )}
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Persona grid */}
          <div className="space-y-2" data-testid="coach-picker-grid">
            {loading ? (
              <div className="py-12 text-center text-slate-400">
                <Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading…
              </div>
            ) : personas.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs">No personas match — broaden the filters.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {personas.map((p) => {
                  const active = selectedId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      className={`text-left rounded-xl border p-3 transition-all ${
                        active
                          ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200'
                          : 'border-slate-200 bg-white hover:border-indigo-200'
                      }`}
                      data-testid={`coach-picker-card-${p.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <CoachAvatar persona={p} size={56} ring={active} />
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-semibold truncate ${active ? 'text-indigo-900' : 'text-slate-900'}`}>
                            {p.name}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5 capitalize">
                            {p.gender.replace('_', '-')} · {p.tone}
                          </div>
                          <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">{p.tagline}</p>
                        </div>
                        {active && <Check className="h-4 w-4 text-indigo-600 shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Voice settings */}
          <div className="space-y-3 rounded-xl border border-slate-200 p-4 bg-slate-50/40 self-start">
            <h3 className="text-xs font-semibold text-slate-900">Voice</h3>
            {!supported && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Your browser doesn't support speech synthesis. Visual persona will still apply.
              </p>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Voice</label>
              <select
                value={voiceUri || ''}
                onChange={(e) => setVoiceUri(e.target.value || null)}
                disabled={!supported || voices.length === 0}
                className="w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                data-testid="coach-picker-voice-select"
              >
                <option value="">— Pick a voice —</option>
                {voices.map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>
                    {v.name} ({v.lang})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 flex justify-between mb-1">
                <span>Speech rate</span>
                <span className="font-mono">{voiceRate.toFixed(2)}×</span>
              </label>
              <input
                type="range"
                min={0.5}
                max={1.6}
                step={0.05}
                value={voiceRate}
                onChange={(e) => setVoiceRate(Number(e.target.value))}
                className="w-full"
                data-testid="coach-picker-rate"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={testVoice} disabled={!supported || muted} data-testid="coach-picker-test">
                {speaking ? <><Square className="h-3 w-3 mr-1" /> Stop</> : <><Play className="h-3 w-3 mr-1" /> Test voice</>}
              </Button>
            </div>
            <div className="border-t border-slate-200 pt-3 space-y-2">
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="coach-picker-auto-speak-label">
                <input
                  type="checkbox"
                  checked={autoSpeak}
                  onChange={(e) => setAutoSpeak(e.target.checked)}
                  data-testid="coach-picker-auto-speak"
                />
                Read coach replies aloud automatically
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="coach-picker-mute-label">
                <input
                  type="checkbox"
                  checked={muted}
                  onChange={(e) => setMuted(e.target.checked)}
                  data-testid="coach-picker-mute"
                />
                Mute voice entirely (override auto-speak)
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <div className="text-[10px] text-slate-500">
            {selectedPersona ? `Selected: ${selectedPersona.name}` : 'No coach selected yet'}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="coach-picker-cancel">Cancel</Button>
            {isOrgAdmin && currentOrgId && (
              <Button
                variant="outline"
                onClick={() => save({ asOrgDefault: true })}
                disabled={!selectedId || saving}
                data-testid="coach-picker-save-org-default"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                Save & set as org default
              </Button>
            )}
            <Button onClick={() => save()} disabled={!selectedId || saving} data-testid="coach-picker-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Save preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
